/**
 * CompleteMobileModal — shown after a first-time Google sign-in when the
 * customer_accounts row has no mobile. Requires a 10-digit mobile to unlock
 * booking + invoice history flows that key off phone number.
 */
import React, { useState } from 'react';
import { Loader2, Phone, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useCustomerAuth, formatApiError } from '../contexts/CustomerAuthContext';

export default function CompleteMobileModal({ open, onClose }) {
  const { completeMobile } = useCustomerAuth();
  const [mobile, setMobile] = useState('');
  const [city, setCity] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const digits = String(mobile).replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) { setErr('Please enter a 10-digit mobile number'); return; }
    setBusy(true); setErr('');
    try {
      await completeMobile({ mobile: digits, city: city.trim() || undefined });
      onClose?.();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="w-[95vw] max-w-md p-0 gap-0 rounded-2xl overflow-hidden" data-testid="complete-mobile-modal">
        <DialogTitle className="sr-only">Complete your profile</DialogTitle>
        <DialogDescription className="sr-only">Add your mobile number to finish setting up your EntoRid account</DialogDescription>
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white px-6 py-5">
          <h2 className="text-xl font-bold">One quick step</h2>
          <p className="text-emerald-100 text-sm mt-1">Add your mobile number so our technicians can reach you.</p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Mobile</Label>
            <div className="relative mt-1">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="9876543210"
                maxLength={10}
                inputMode="numeric"
                className="pl-9 h-11"
                data-testid="complete-mobile-input"
                required
                autoFocus
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">City (optional)</Label>
            <Input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Dehradun"
              className="h-11 mt-1"
              data-testid="complete-mobile-city"
            />
          </div>
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 inline-flex items-start gap-1.5" data-testid="complete-mobile-error">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> {err}
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="complete-mobile-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : 'Save & Continue'}
          </Button>
          <p className="text-[10px] text-slate-400 text-center">
            Your number stays private and is used only for booking updates.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
